import { API_URL } from "./utils";

/**
 * Default interval in milliseconds to check if server is back online.
 * Configured as 30 seconds (30000 ms) as requested.
 */
export const CHECK_SERVER_INTERVAL_MS = 30000;

class ServerHealthService {
  private timer: any = null;
  private isChecking = false;
  private isPollingActive = false;
  private isServerOnline = false;

  /**
   * Ping the dummy health-check endpoint on the backend.
   * If server is already known to be online, returns true immediately without calling API.
   * Pass force = true to force an actual network request.
   */
  public async pingServer(force: boolean = false): Promise<boolean> {
    if (!force && this.isServerOnline) {
      return true;
    }

    if (this.isChecking) return this.isServerOnline;
    this.isChecking = true;

    try {
      const pingUrl = `${API_URL}/health`;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);

      const response = await fetch(pingUrl, {
        method: "GET",
        signal: controller.signal,
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      });

      clearTimeout(timeoutId);

      // Status 200 means server is up and responsive
      if (response.ok || (response.status >= 200 && response.status < 300)) {
        this.markOnline();
        return true;
      }
      this.markOffline();
      return false;
    } catch (err) {
      // Network error, connection refused, or timeout
      this.markOffline();
      return false;
    } finally {
      this.isChecking = false;
    }
  }

  /**
   * Mark server as online and stop all polling.
   */
  public markOnline() {
    this.isServerOnline = true;
    this.stopServerReconnectionPolling();
  }

  /**
   * Mark server as offline.
   */
  public markOffline() {
    this.isServerOnline = false;
  }

  public getIsOnline(): boolean {
    return this.isServerOnline;
  }

  /**
   * Start polling the ping API every CHECK_SERVER_INTERVAL_MS.
   * When server is reachable again:
   * 1. Stops polling.
   * 2. Triggers bulk offline attendance sync ("trigger-attendance-sync").
   * 3. Triggers employee user sync ("trigger-employee-sync").
   */
  public startServerReconnectionPolling(intervalMs: number = CHECK_SERVER_INTERVAL_MS) {
    if (this.isPollingActive) {
      console.log("[ServerHealth] Reconnection polling is already in progress.");
      return;
    }

    console.log(`[ServerHealth] Starting reconnection check every ${intervalMs} ms...`);
    this.isPollingActive = true;

    const checkAndSchedule = async () => {
      const isOnline = await this.pingServer(true);
      console.log(`[ServerHealth] Ping check result: ${isOnline ? "ONLINE" : "OFFLINE"}`);

      if (isOnline) {
        console.log("[ServerHealth] Server is back online! Triggering bulk-offline and sync-data APIs...");
        this.stopServerReconnectionPolling();

        // 1. Trigger bulk offline attendance sync
        window.dispatchEvent(new Event("trigger-attendance-sync"));

        // 2. Trigger employee user sync (/sync-data)
        window.dispatchEvent(new Event("trigger-employee-sync"));
      } else {
        // Schedule next check
        this.timer = setTimeout(checkAndSchedule, intervalMs);
      }
    };

    // First check after intervalMs
    this.timer = setTimeout(checkAndSchedule, intervalMs);
  }

  /**
   * Stop polling
   */
  public stopServerReconnectionPolling() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.isPollingActive = false;
  }

  public isPolling(): boolean {
    return this.isPollingActive;
  }
}

export const serverHealthService = new ServerHealthService();
